import { Input } from "@/components/ui/input";
import { Label } from "@radix-ui/react-label";
import { ComponentProps, ReactElement, useId } from "react";
import { ComboBoxRange, FilterColumn } from "..";
import EmployeeFilterComboBox from "../combo-box/Employee";
import ClientFilterComboBox from "../combo-box/Client";
import SupplierFilterComboBox from "../combo-box/Supplier";
import ArEmployeeFilterComboBox from "../combo-box/ArEmployee";

type FilterFieldBaseProps = Omit<
    ComponentProps<typeof Input>,
    "onChange" | "value"
>;

interface ComboBoxFilterFieldProps<T> extends FilterFieldBaseProps {
    column: FilterColumn<T>;
    value: string;
    onChange: (value: string) => void;
}

export default function ComboBoxFilterField<T>({
    column,
    value,
    onChange,
    placeholder = "Enter value..",
    ...rest
}: ComboBoxFilterFieldProps<T>) {
    const valueId = useId();

    const parsedValue: ComboBoxRange = (() => {
        try {
            return JSON.parse(value ?? '{"id":null,"value":null}');
        } catch {
            return { id: null, value: null };
        }
    })();

    const emitChange = (next: ComboBoxRange) => {
        onChange(JSON.stringify(next));
    };

    let comboBox: ReactElement | null = null;

    if (column.filterId === "employees.full_name") {
        comboBox = (
            <EmployeeFilterComboBox
                value={parsedValue.id || ""}
                onChange={(id) =>
                    emitChange({ ...parsedValue, id: id || null })
                }
                placeholder="Select employee..."
                disabled={rest.disabled}
                label="Employee"
            />
        );
    } else if (
        column.filterId === "clients.full_name" ||
        column.filterId === "quotation_to.full_name"
    ) {
        comboBox = (
            <ClientFilterComboBox
                value={parsedValue.id || ""}
                onChange={(id) =>
                    emitChange({ ...parsedValue, id: id || null })
                }
                placeholder="Select client..."
                disabled={rest.disabled}
                label="Client"
            />
        );
    } else if (column.filterId === "suppliers.full_name") {
        comboBox = (
            <SupplierFilterComboBox
                value={parsedValue.id || ""}
                onChange={(id) =>
                    emitChange({ ...parsedValue, id: id || null })
                }
                placeholder="Select supplier..."
                disabled={rest.disabled}
                label="Supplier"
            />
        );
    } else if (column.filterId === "ar_coordinators.full_name") {
        comboBox = (
            <ArEmployeeFilterComboBox
                value={parsedValue.id || ""}
                onChange={(id) =>
                    emitChange({ ...parsedValue, id: id || null })
                }
                placeholder="Select coordinator..."
                disabled={rest.disabled}
                label="AR Coordinator"
            />
        );
    } else if (column.filterId === "supporting_coordinators.full_name") {
        comboBox = (
            <ArEmployeeFilterComboBox
                value={parsedValue.id || ""}
                onChange={(id) =>
                    emitChange({ ...parsedValue, id: id || null })
                }
                placeholder="Select coordinator..."
                disabled={rest.disabled}
                label="AR Supporting Coordinator"
            />
        );
    }

    return (
        <div className={"flex flex-col gap-2"}>
            {comboBox}
            <Label className="text-sm font-medium">{column.headingText}</Label>

            <Input
                id={valueId}
                placeholder={placeholder}
                value={parsedValue.value ?? ""}
                onChange={(e) =>
                    emitChange({
                        ...parsedValue,
                        value: e.target.value || null,
                    })
                }
                {...rest}
            />
        </div>
    );
}
